import type { Brand, Website } from '@integration-layer/index'
/**
 * Checks if a given website is a valid brand.
 *
 * @param {string} website - The website/brand to validate
 * @returns {boolean} True if the website is a valid brand, false otherwise
 *
 * @example
 *  Returns true for 'armani'
 *  Returns false for 'invalidBrand'
 */
const checkWebsite = (website: string) => {
  return brands.includes(website as Website)
}

/**
 * Sets the current website in the app configuration.
 *
 * @param {string | string[]} currentWebsite - The website to set
 * @throws {Error} If an invalid website is provided
 *
 * @description
 * - If the website is not valid, defaults to 'global'
 * - Otherwise, sets the website to the provided brand
 *
 * @example
 *  Sets website to 'armani' if valid
 *  Sets website to 'global' if invalid
 */
const setWebsite = (currentWebsite: string | string[]) => {
  if (!checkWebsite(currentWebsite as string)) {
    useAppConfig().website = 'global'
    return
  }
  // else we set website
  useAppConfig().website = currentWebsite as Brand
}

/**
 * Middleware to determine and set the current website/brand.
 *
 * @param {Object} to - The route being navigated to
 * @param {Object} to.params - Route parameters
 * @param {string} [to.params.website] - Optional website parameter
 * @param {string} to.path - Full route path
 *
 * @returns {void}
 *
 * @description
 * - Extracts website from route parameters or path
 * - Sets the website in the app configuration
 *
 * @example
 * // For route '/en/armani/products'
 * // Sets website to 'armani'
 */
export default defineNuxtRouteMiddleware(async to => {
  const currentWebsite = to.params?.website ?? to.path.split('/')[2]
  setWebsite(currentWebsite)
})
